import {z} from 'zod'
import {useMemo} from 'react'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {useGetPreviousReportByProject} from '@/features/medical-reports/queries/useGetPreviousReportByProject'
import {MedicalReportFieldsSchema, REPORT_TEXTAREA_LIMIT} from '@/features/medical-reports/formSchemas'
import {MedicalReportSectionTitle} from '@/features/medical-reports/components/medical-report-section-title/MedicalReportSectionTitle'
import {MedicalReportTextArea} from '@/features/medical-reports/components/medical-report-text-area/MedicalReportTextArea'

const MedicalReportNoteValidationSchema = MedicalReportFieldsSchema.pick({
    notes: true,
    project: true,
    reportType: true
})
type MedicalReportNoteValidationSchema = z.infer<typeof MedicalReportNoteValidationSchema>

export const MedicalReportNoteSection = ({defaultValue}: {defaultValue?: string}) => {
    const {t} = useTranslation()
    const form = useFormContext<MedicalReportNoteValidationSchema>()
    const projectId = useWatch({control: form.control, name: 'project.id'})
    const medicalReportTypeId = useWatch({control: form.control, name: 'reportType.id'})

    const title = useMemo(() => t('commons:notes'), [])
    const description = useMemo(() => t('medical_records:notes_report_description'), [])

    const previousProjectReportQuery = useGetPreviousReportByProject({
        projectId,
        medicalReportTypeId,
        options: {enabled: false}
    })

    const previousReport = previousProjectReportQuery?.data?.medicalRecordReportAttributes?.filter(
        attribute => attribute.name == 'notes'
    )?.[0]?.value

    return (
        <>
            <MedicalReportSectionTitle title={t('commons:report', {count: 1})} />
            <FormSection title={title} description={description}>
                <Controller
                    control={form.control}
                    name="notes"
                    render={({field, fieldState}) => (
                        <MedicalReportTextArea
                            rows={4}
                            maxLength={REPORT_TEXTAREA_LIMIT}
                            title={title}
                            description={description}
                            placeholder={t('commons:description_dot')}
                            errorMessage={fieldState.error?.message}
                            onChange={field.onChange}
                            previousReport={previousReport}
                            defaultValue={defaultValue}
                            currentField="notes"
                            value={field.value ?? ''}
                        />
                    )}
                />
            </FormSection>
        </>
    )
}
